/**
 * Verifier Portal — third-party assurance access (ESRS 2 GOV-5 / ISAE 3000)
 *
 * Two personas on one surface:
 *   · Engagement admin — grants a read-only, time-boxed engagement to a
 *     verifier account (must hold the "auditor" role; creator ≠ verifier).
 *   · Verifier — opens their own engagement and reads the engagement-scoped
 *     claim→evidence traceability matrix and the evidence register with
 *     content hashes. Every portal read is itself audit-logged.
 *
 * Backend contract (backend/routes/verifier_routes.py):
 *   POST /api/v1/verifier/engagements                      → engagement
 *   GET  /api/v1/verifier/engagements?entity_id=            → { engagements }
 *   GET  /api/v1/verifier/engagements/mine                  → { engagements }
 *   POST /api/v1/verifier/engagements/{id}/revoke           → engagement
 *   GET  /api/v1/verifier/portal/{id}/summary               → { engagement,
 *          claims:{total,by_status}, evidence:{total,by_approval_status},
 *          requirement_traceability:[{claim_id,requirement_code,claim_status,
 *          evidence_total,evidence_approved,evidence_ids}] }
 *   GET  /api/v1/verifier/portal/{id}/evidence              → { evidence:
 *          [{evidence_id,name,filename,evidence_type,source,content_hash,
 *          approval_status,verified,verified_by,requirement_ids,
 *          version_count}] }
 * Portal access requires identity match + active status + unexpired window;
 * the backend answers 403 with the specific reason otherwise.
 */
import { useCallback, useEffect, useState } from "react";
import {
  ShieldCheck, FileSearch, Link2, Hash, Eye, Ban, Plus, X, CheckCircle2,
  AlertTriangle, XCircle, BadgeCheck, Clock, ClipboardList, Scale, Inbox,
} from "lucide-react";
import {
  Page, PageHeader, Card, Overline, Metric, Badge, Data, Btn, DataTable,
  TabBar, EmptyState, ErrorBanner, Skeleton, field, Label, apiFetch, fmt,
  C, MONO, SANS,
} from "../../components/esg/EsgKit";
import { useEntities } from "../../hooks/useEntities";
import TracedValue from "../../components/TracedValue";

const BASE = "/api/v1/verifier";

function statusBadge(status) {
  switch (status) {
    case "active":
      return <Badge c={C.primary} Icon={CheckCircle2} label="Active" />;
    case "revoked":
      return <Badge c={C.destructive} Icon={Ban} label="Revoked" />;
    case "completed":
      return <Badge c={C.locked} Icon={ShieldCheck} label="Completed" />;
    default:
      return <Badge c={C.neutral} Icon={Clock} label={status || "—"} />;
  }
}

// ISAE 3000 engagement type — reasonable assurance is the stronger opinion.
function assuranceBadge(level) {
  return level === "reasonable" ? (
    <Badge c={C.locked} Icon={ShieldCheck} label="Reasonable" />
  ) : (
    <Badge c={C.neutral} Icon={Scale} label="Limited" />
  );
}

function approvalBadge(status) {
  switch (status) {
    case "approved":
      return <Badge c={C.primary} Icon={CheckCircle2} label="Approved" />;
    case "rejected":
      return <Badge c={C.destructive} Icon={XCircle} label="Rejected" />;
    case "pending":
      return <Badge c={C.accent} Icon={Clock} label="Pending" />;
    default:
      return <Badge c={C.neutral} Icon={Clock} label={status || "—"} />;
  }
}

function coverageBadge(approved, total) {
  if (!total) return <Badge c={C.destructive} Icon={XCircle} label="No evidence" />;
  if (approved >= total) return <Badge c={C.primary} Icon={CheckCircle2} label="Covered" />;
  return <Badge c={C.accent} Icon={AlertTriangle} label="Partial" />;
}

// Expiry window: date + remaining days, colored as it closes.
function ExpiryCell({ iso }) {
  if (!iso) return <Data color={C.muted}>—</Data>;
  const exp = new Date(iso);
  const days = Math.ceil((exp.getTime() - Date.now()) / 86400000);
  const color = days < 0 ? C.destructive : days <= 7 ? C.accent : C.body;
  const rel = days < 0 ? "expired" : days === 0 ? "today" : `in ${days}d`;
  return (
    <span style={{ whiteSpace: "nowrap" }}>
      <Data color={C.body}>{exp.toISOString().slice(0, 10)}</Data>{" "}
      <Data color={color} weight={700}>· {rel}</Data>
    </span>
  );
}

const hashStyle = {
  fontFamily: MONO, fontSize: 11, color: C.body,
  display: "inline-flex", alignItems: "center", gap: 4,
};

const EMPTY_FORM = {
  verifier_user_id: "",
  verifier_org: "",
  framework: "CSRD",
  reporting_period: "FY2026",
  assurance_level: "limited",
  expires_at: "",
};

export default function VerifierPortal() {
  const { entities, entityId, setEntityId } = useEntities("verifier_entity_id");
  const [tab, setTab] = useState("engagements");

  // Admin side
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [revoking, setRevoking] = useState("");

  // Verifier side
  const [mine, setMine] = useState([]);
  const [portalId, setPortalId] = useState("");
  const [summary, setSummary] = useState(null);
  const [evidence, setEvidence] = useState([]);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState("");

  const loadEngagements = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    setError("");
    try {
      const r = await apiFetch(`${BASE}/engagements?entity_id=${encodeURIComponent(entityId)}`);
      setEngagements(r?.engagements || []);
    } catch (e) {
      setError(e.message || "Failed to load engagements");
      setEngagements([]);
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  const loadMine = useCallback(async () => {
    try {
      const r = await apiFetch(`${BASE}/engagements/mine`);
      const list = r?.engagements || [];
      setMine(list);
      setPortalId((cur) => cur || list.find((e) => e.status === "active")?.engagement_id || "");
    } catch {
      setMine([]);
    }
  }, []);

  useEffect(() => { loadEngagements(); }, [loadEngagements]);
  useEffect(() => { loadMine(); }, [loadMine]);

  const loadPortal = useCallback(async () => {
    if (!portalId) {
      setSummary(null);
      setEvidence([]);
      return;
    }
    setPortalLoading(true);
    setPortalError("");
    try {
      const [s, ev] = await Promise.all([
        apiFetch(`${BASE}/portal/${encodeURIComponent(portalId)}/summary`),
        apiFetch(`${BASE}/portal/${encodeURIComponent(portalId)}/evidence`),
      ]);
      setSummary(s || null);
      setEvidence(ev?.evidence || []);
    } catch (e) {
      // 403 carries the exact reason (not your engagement / revoked / expired)
      setPortalError(e.message || "Portal access denied");
      setSummary(null);
      setEvidence([]);
    } finally {
      setPortalLoading(false);
    }
  }, [portalId]);

  useEffect(() => { loadPortal(); }, [loadPortal]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!entityId || !form.expires_at) return;
    setSaving(true);
    setError("");
    try {
      await apiFetch(`${BASE}/engagements`, {
        method: "POST",
        body: {
          entity_id: entityId,
          verifier_user_id: form.verifier_user_id.trim(),
          verifier_org: form.verifier_org.trim(),
          framework: form.framework.trim(),
          reporting_period: form.reporting_period.trim(),
          assurance_level: form.assurance_level,
          expires_at: new Date(form.expires_at).toISOString(),
        },
      });
      setForm(EMPTY_FORM);
      setShowForm(false);
      await loadEngagements();
    } catch (err) {
      setError(err.message || "Failed to create engagement");
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (engagementId) => {
    setRevoking(engagementId);
    setError("");
    try {
      await apiFetch(`${BASE}/engagements/${encodeURIComponent(engagementId)}/revoke`, { method: "POST" });
      await loadEngagements();
      await loadMine();
    } catch (err) {
      setError(err.message || "Failed to revoke engagement");
    } finally {
      setRevoking("");
    }
  };

  const engagementColumns = [
    {
      key: "engagement_id",
      label: "Engagement",
      render: (r) => <Data color={C.ink}>{r.engagement_id}</Data>,
    },
    {
      key: "verifier",
      label: "Verifier",
      render: (r) => (
        <span>
          <Data color={C.body}>{r.verifier_user_id}</Data>
          {r.verifier_org && (
            <span style={{ fontFamily: SANS, fontSize: 11, color: C.muted }}> · {r.verifier_org}</span>
          )}
        </span>
      ),
    },
    {
      key: "scope",
      label: "Scope",
      render: (r) => <Data color={C.body}>{r.framework} · {r.reporting_period}</Data>,
    },
    { key: "assurance_level", label: "Assurance", render: (r) => assuranceBadge(r.assurance_level) },
    { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    { key: "expires_at", label: "Window", render: (r) => <ExpiryCell iso={r.expires_at} /> },
    {
      key: "actions",
      label: "",
      align: "right",
      render: (r) =>
        r.status === "active" ? (
          <Btn
            variant="destructive"
            Icon={Ban}
            disabled={revoking === r.engagement_id}
            onClick={() => revoke(r.engagement_id)}
            title="Revoke access immediately"
          >
            {revoking === r.engagement_id ? "Revoking…" : "Revoke"}
          </Btn>
        ) : null,
    },
  ];

  const traceColumns = [
    {
      key: "requirement_code",
      label: "Requirement",
      render: (r) => <Data color={C.ink} size={13}>{r.requirement_code || "—"}</Data>,
    },
    {
      key: "claim_id",
      label: "Claim",
      render: (r) => <Data color={C.muted} size={11}>{r.claim_id}</Data>,
    },
    {
      key: "claim_status",
      label: "Claim status",
      render: (r) => <Badge c={C.neutral} Icon={ClipboardList} label={r.claim_status || "—"} />,
    },
    {
      key: "evidence",
      label: "Evidence approved / linked",
      align: "right",
      render: (r) => (
        <Data color={r.evidence_approved < r.evidence_total ? C.accent : C.body}>
          {fmt(r.evidence_approved, 0)} / {fmt(r.evidence_total, 0)}
        </Data>
      ),
    },
    {
      key: "coverage",
      label: "Coverage",
      render: (r) => coverageBadge(r.evidence_approved, r.evidence_total),
    },
  ];

  const evidenceColumns = [
    {
      key: "name",
      label: "Evidence",
      render: (r) => (
        <span>
          {/* Phase 4: verifier clicks through to hash integrity + provenance */}
          <TracedValue value={r.name || r.evidence_id} type="evidence"
            id={r.evidence_id} color={C.ink} />
          {r.filename && (
            <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted }}>{r.filename}</div>
          )}
        </span>
      ),
    },
    {
      key: "evidence_type",
      label: "Type · source",
      render: (r) => (
        <Data color={C.body}>
          {r.evidence_type || "—"}{r.source ? ` · ${r.source}` : ""}
        </Data>
      ),
    },
    {
      key: "content_hash",
      label: "Content hash",
      render: (r) =>
        r.content_hash ? (
          <span style={hashStyle} title={r.content_hash}>
            <Hash size={11} strokeWidth={1.5} color={C.muted} />
            {r.content_hash.slice(0, 16)}…
          </span>
        ) : (
          <Data color={C.muted}>—</Data>
        ),
    },
    { key: "approval_status", label: "Approval", render: (r) => approvalBadge(r.approval_status) },
    {
      key: "verified",
      label: "Verified",
      render: (r) =>
        r.verified ? (
          <Badge c={C.primary} Icon={BadgeCheck} label={r.verified_by ? `By ${r.verified_by}` : "Verified"} />
        ) : (
          <Badge c={C.neutral} Icon={Clock} label="Unverified" />
        ),
    },
    {
      key: "requirement_ids",
      label: "Requirements",
      render: (r) => (
        <Data color={C.body} size={11}>
          {(r.requirement_ids || []).join(", ") || "—"}
        </Data>
      ),
    },
    {
      key: "version_count",
      label: "Versions",
      align: "right",
      render: (r) => <Data color={C.body}>{fmt(r.version_count, 0)}</Data>,
    },
  ];

  const header = (
    <PageHeader
      overline="Assurance · Third-Party Verifier Access"
      OverlineIcon={ShieldCheck}
      title="Verifier Portal"
      subtitle="Grant time-boxed, read-only assurance engagements (ISAE 3000 limited or reasonable) and give the engaged verifier a scoped view: claim→evidence traceability per ESRS requirement, and the evidence register with content hashes. Every portal read is itself recorded in the audit trail."
      entities={entities}
      entityId={entityId}
      onEntityChange={setEntityId}
      onRefresh={tab === "portal" ? loadPortal : loadEngagements}
      loading={tab === "portal" ? portalLoading : loading}
    />
  );

  const s = summary || {};
  const claimsByStatus = s.claims?.by_status || {};
  const evidenceByStatus = s.evidence?.by_approval_status || {};
  const trace = s.requirement_traceability || [];
  const approvedEvidence = evidenceByStatus.approved || 0;

  return (
    <Page>
      {header}

      <TabBar
        tabs={[
          { id: "engagements", label: "Engagements", count: engagements.length },
          { id: "portal", label: "My portal", count: mine.length },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === "engagements" && (
        <div style={{ display: "grid", gap: 16 }}>
          <ErrorBanner message={error} onDismiss={() => setError("")} />

          {!entityId ? (
            <Card>
              <EmptyState
                Icon={ShieldCheck}
                title="No reporting entity selected"
                body="Choose a reporting entity to manage its third-party verifier engagements."
              />
            </Card>
          ) : loading && engagements.length === 0 ? (
            <Skeleton h={220} />
          ) : (
            <>
              <Card>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                  <Overline Icon={FileSearch} style={{ marginBottom: 0 }}>
                    Engagement register
                  </Overline>
                  <Btn
                    variant={showForm ? "ghost" : "secondary"}
                    Icon={showForm ? X : Plus}
                    onClick={() => setShowForm((v) => !v)}
                  >
                    {showForm ? "Cancel" : "Grant engagement"}
                  </Btn>
                </div>

                {showForm && (
                  <form onSubmit={submit} style={{ marginTop: 16 }}>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                        gap: 14,
                      }}
                    >
                      <div>
                        <Label>Verifier user ID *</Label>
                        <input
                          value={form.verifier_user_id}
                          onChange={(e) => setField("verifier_user_id", e.target.value)}
                          required
                          placeholder="usr_a1b2c3d4e5f6"
                          style={field}
                        />
                      </div>
                      <div>
                        <Label>Verifier organisation</Label>
                        <input
                          value={form.verifier_org}
                          onChange={(e) => setField("verifier_org", e.target.value)}
                          placeholder="Example Assurance LLP"
                          style={field}
                        />
                      </div>
                      <div>
                        <Label>Framework *</Label>
                        <input
                          value={form.framework}
                          onChange={(e) => setField("framework", e.target.value)}
                          required
                          placeholder="CSRD"
                          style={field}
                        />
                      </div>
                      <div>
                        <Label>Reporting period *</Label>
                        <input
                          value={form.reporting_period}
                          onChange={(e) => setField("reporting_period", e.target.value)}
                          required
                          placeholder="FY2026"
                          style={field}
                        />
                      </div>
                      <div>
                        <Label>Assurance level (ISAE 3000) *</Label>
                        <select
                          value={form.assurance_level}
                          onChange={(e) => setField("assurance_level", e.target.value)}
                          required
                          style={field}
                        >
                          <option value="limited">Limited</option>
                          <option value="reasonable">Reasonable</option>
                        </select>
                      </div>
                      <div>
                        <Label>Access expires *</Label>
                        <input
                          type="datetime-local"
                          value={form.expires_at}
                          onChange={(e) => setField("expires_at", e.target.value)}
                          required
                          style={field}
                        />
                      </div>
                    </div>
                    <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                      <Btn
                        type="submit"
                        Icon={Plus}
                        disabled={saving || !form.verifier_user_id.trim() || !form.framework.trim() || !form.expires_at}
                      >
                        {saving ? "Granting…" : "Grant access"}
                      </Btn>
                      <Btn variant="secondary" onClick={() => { setForm(EMPTY_FORM); setShowForm(false); }}>
                        Reset
                      </Btn>
                    </div>
                    <p style={{ fontFamily: SANS, fontSize: 11, color: C.body, margin: "10px 0 0" }}>
                      The verifier account must already hold the read-only auditor role, and — separation
                      of duties — you cannot grant an engagement to yourself.
                    </p>
                  </form>
                )}

                <div style={{ marginTop: showForm ? 20 : 16 }}>
                  {engagements.length === 0 ? (
                    <EmptyState
                      Icon={Inbox}
                      title="No engagements for this entity"
                      body="Grant a time-boxed engagement to give an external verifier read-only access to this entity's claims and evidence."
                    />
                  ) : (
                    <DataTable
                      columns={engagementColumns}
                      rows={engagements}
                      keyField="engagement_id"
                      emptyLabel="No engagements"
                    />
                  )}
                </div>
              </Card>
            </>
          )}
        </div>
      )}

      {tab === "portal" && (
        <div style={{ display: "grid", gap: 16 }}>
          <ErrorBanner message={portalError} onDismiss={() => setPortalError("")} />

          {mine.length === 0 ? (
            <Card>
              <EmptyState
                Icon={Eye}
                title="No engagements assigned to your account"
                body="The portal view is scoped to the engaged verifier. When an entity grants your account an assurance engagement, it appears here."
              />
            </Card>
          ) : (
            <>
              <Card>
                <Overline Icon={FileSearch}>My engagements</Overline>
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <select
                    value={portalId}
                    onChange={(e) => setPortalId(e.target.value)}
                    aria-label="Engagement"
                    style={{ ...field, width: "auto", minWidth: 320 }}
                  >
                    <option value="" disabled>Select engagement…</option>
                    {mine.map((e) => (
                      <option key={e.engagement_id} value={e.engagement_id}>
                        {e.engagement_id} — {e.framework} {e.reporting_period} ({e.status})
                      </option>
                    ))}
                  </select>
                  {s.engagement && (
                    <>
                      {statusBadge(s.engagement.status)}
                      {assuranceBadge(s.engagement.assurance_level)}
                      <ExpiryCell iso={s.engagement.expires_at} />
                    </>
                  )}
                </div>
                <p
                  style={{
                    display: "flex", alignItems: "center", gap: 6,
                    fontFamily: SANS, fontSize: 11, color: C.muted, margin: "12px 0 0",
                  }}
                >
                  <Eye size={12} strokeWidth={1.5} />
                  Every portal view is recorded in the entity's audit trail — under ISAE 3000 your
                  review trail is part of the engagement evidence.
                </p>
              </Card>

              {portalLoading && !summary ? (
                <div style={{ display: "grid", gap: 16 }}>
                  <Skeleton h={110} />
                  <Skeleton h={240} />
                  <Skeleton h={240} />
                </div>
              ) : summary ? (
                <>
                  {/* Engagement-scoped readiness rollup */}
                  <Card>
                    <Overline Icon={ClipboardList}>
                      Engagement summary · {s.engagement?.framework} {s.engagement?.reporting_period}
                    </Overline>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                        gap: 20,
                      }}
                    >
                      <Metric label="Claims in scope" value={fmt(s.claims?.total, 0)} />
                      <Metric label="Evidence items" value={fmt(s.evidence?.total, 0)} />
                      <Metric
                        label="Evidence approved"
                        value={fmt(approvedEvidence, 0)}
                        color={approvedEvidence === (s.evidence?.total || 0) && approvedEvidence > 0 ? C.primary : C.ink}
                      />
                      <Metric
                        label="Requirements traced"
                        value={fmt(trace.length, 0)}
                        color={C.locked}
                      />
                    </div>
                    {(Object.keys(claimsByStatus).length > 0 || Object.keys(evidenceByStatus).length > 0) && (
                      <div
                        style={{
                          marginTop: 16, display: "flex", gap: 20, flexWrap: "wrap",
                          fontFamily: SANS, fontSize: 12, color: C.body,
                        }}
                      >
                        {Object.entries(claimsByStatus).map(([k, v]) => (
                          <span key={`c-${k}`}>
                            Claims {k} <Data color={C.ink}>{fmt(v, 0)}</Data>
                          </span>
                        ))}
                        {Object.entries(evidenceByStatus).map(([k, v]) => (
                          <span key={`e-${k}`}>
                            Evidence {k} <Data color={k === "approved" ? C.primary : C.ink}>{fmt(v, 0)}</Data>
                          </span>
                        ))}
                      </div>
                    )}
                  </Card>

                  {/* Claim → evidence traceability, per ESRS requirement */}
                  <Card accent={C.locked}>
                    <Overline Icon={Link2} color={C.locked}>
                      Requirement traceability matrix
                    </Overline>
                    {trace.length === 0 ? (
                      <EmptyState
                        Icon={Link2}
                        title="No claims in the engagement scope"
                        body="No claims exist for this entity, framework and reporting period yet, so there is nothing to trace."
                      />
                    ) : (
                      <DataTable
                        columns={traceColumns}
                        rows={trace}
                        keyField="claim_id"
                        emptyLabel="No claims in scope"
                      />
                    )}
                  </Card>

                  {/* Evidence register with integrity hashes */}
                  <Card>
                    <Overline Icon={Hash}>Evidence register</Overline>
                    {evidence.length === 0 ? (
                      <EmptyState
                        Icon={Inbox}
                        title="No evidence for this entity"
                        body="The entity has not registered evidence yet. Evidence appears here with its content hash, approval state and linked requirements."
                      />
                    ) : (
                      <DataTable
                        columns={evidenceColumns}
                        rows={evidence}
                        keyField="evidence_id"
                        emptyLabel="No evidence"
                      />
                    )}
                  </Card>
                </>
              ) : !portalError ? (
                <Card>
                  <EmptyState
                    Icon={FileSearch}
                    title="Select an engagement"
                    body="Choose one of your engagements to open its scoped portal view."
                  />
                </Card>
              ) : null}
            </>
          )}
        </div>
      )}
    </Page>
  );
}
